import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Lock, Eye, EyeOff, KeyRound, Check, AlertCircle, Loader2, X, ShieldAlert } from 'lucide-react';
import ObexRequestHandlerP80 from '../../../handlers/ObexRequestsHandlerP80';
import { useToast } from '../../../hooks/useToast';
import './ChangePasswordModal.css';

export interface ChangePasswordModalProps {
  isOpen: boolean;
  accessToken: string;
  initialCurrentPassword?: string;
  onSuccess: () => void;
  onCancel?: () => void;
  isMandatory?: boolean;
}

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  accessToken,
  initialCurrentPassword = '',
  onSuccess,
  onCancel,
  isMandatory = true,
}) => {
  const { notify } = useToast();
  const [currentPassword, setCurrentPassword] = useState(initialCurrentPassword);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setCurrentPassword(initialCurrentPassword || '');
      setNewPassword('');
      setConfirmPassword('');
      setErrorMessage(null);
      setIsLoading(false);
    }
  }, [isOpen, initialCurrentPassword]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onCancel && !isMandatory) {
        onCancel();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel, isMandatory]);

  if (!isOpen) return null;

  // Validation rules
  const hasMinLength = newPassword.length >= 8;
  const hasNumberOrSpecial = /[0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword);
  const hasLetter = /[a-zA-Z]/.test(newPassword);
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const isDifferentFromCurrent = !currentPassword || newPassword !== currentPassword;
  const isFormValid =
    currentPassword.trim().length > 0 &&
    hasMinLength &&
    hasNumberOrSpecial &&
    hasLetter &&
    passwordsMatch &&
    isDifferentFromCurrent;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;

    if (!currentPassword) {
      setErrorMessage('Por favor introduce tu contraseña actual.');
      return;
    }

    if (!hasMinLength) {
      setErrorMessage('La nueva contraseña debe tener al menos 8 caracteres.');
      return;
    }

    if (!passwordsMatch) {
      setErrorMessage('Las contraseñas no coinciden.');
      return;
    }

    if (!isDifferentFromCurrent) {
      setErrorMessage('La nueva contraseña debe ser diferente de la actual.');
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    try {
      let response = await ObexRequestHandlerP80.get('/auth-change-password', {
        access_token: accessToken,
        current_password: currentPassword,
        new_password: newPassword,
      });

      if (response && response.data) {
        response = response.data;
      }

      const pwdResp = response?.response ?? response?.result?.response ?? response?.data?.result?.response;
      
      const isSuccess =
        (typeof pwdResp === 'string' && pwdResp.toLowerCase().includes('exitosa')) ||
        (typeof pwdResp?.message === 'string' && pwdResp.message.toLowerCase().includes('exitosa')) ||
        pwdResp?.status === 'ok' ||
        pwdResp?.status === true ||
        (response?.success === true && response?.data?.status === true && !pwdResp?.errors);

      if (isSuccess) {
        notify('Contraseña Actualizada', 'Tu contraseña ha sido modificada exitosamente.', 'info');
        onSuccess();
      } else {
        const rawError =
          (typeof pwdResp?.message === 'string' && !isSuccess ? pwdResp.message : null) ||
          (typeof pwdResp?.errors === 'string' ? pwdResp.errors : null) ||
          (typeof pwdResp === 'string' && !isSuccess && pwdResp.trim() !== '' ? pwdResp : null) ||
          response?.message ||
          response?.data?.message ||
          'Credenciales inválidas o contraseña actual incorrecta.';
        
        const friendlyError =
          typeof rawError === 'string'
            ? rawError
            : 'Error al cambiar la contraseña.';
        
        setErrorMessage(friendlyError);
        notify('Error al cambiar contraseña', friendlyError, 'urgent');
      }
    } catch (error) {
      console.error('Change password failed', error);
      const errMsg = error?.response?.data?.response || error?.message || 'Error de conexión al actualizar la contraseña.';
      setErrorMessage(String(errMsg));
      notify('Error', String(errMsg), 'urgent');
    } finally {
      setIsLoading(false);
    }
  };

  return createPortal(
    <div className="change-pwd-overlay">
      <div className="change-pwd-wrapper">
        <div className="change-pwd-modal">
          {/* Header */}
          <div className="change-pwd-header">
            <div className="change-pwd-badge">
              <KeyRound size={20} className="change-pwd-badge-icon" />
            </div>
            <div className="change-pwd-header-text">
              <div className="change-pwd-title-row">
                <h3 className="change-pwd-title">ACTUALIZACIÓN DE CONTRASEÑA</h3>
                {isMandatory && (
                  <span className="change-pwd-tag-mandatory">REQUERIDO</span>
                )}
              </div>
              <p className="change-pwd-subtitle">
                {isMandatory
                  ? 'Por motivos de seguridad, es obligatorio cambiar tu contraseña para continuar.'
                  : 'Introduce tu contraseña actual y define una nueva para tu cuenta.'}
              </p>
            </div>
            {!isMandatory && onCancel && (
              <button
                type="button"
                className="change-pwd-close-btn"
                onClick={onCancel}
                disabled={isLoading}
                aria-label="Cerrar modal"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="change-pwd-body">
            {isMandatory && (
              <div className="change-pwd-notice">
                <ShieldAlert size={16} className="change-pwd-notice-icon" />
                <span>
                  Tu cuenta requiere una renovación de credenciales antes del primer acceso o tras un restablecimiento administrativo.
                </span>
              </div>
            )}

            {errorMessage && (
              <div className="change-pwd-error-banner">
                <AlertCircle size={16} className="change-pwd-error-icon" />
                <span>{errorMessage}</span>
              </div>
            )}

            {/* Contraseña Actual */}
            <div className="change-pwd-form-group">
              <label className="change-pwd-label">Contraseña Actual</label>
              <div className="change-pwd-input-wrap">
                <Lock size={16} className="change-pwd-input-icon" />
                <input
                  required
                  disabled={isLoading}
                  type={showCurrentPassword ? 'text' : 'password'}
                  className="change-pwd-input"
                  placeholder="Introduce tu contraseña actual"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="change-pwd-eye-btn"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  tabIndex={-1}
                  aria-label={showCurrentPassword ? 'Ocultar' : 'Mostrar'}
                >
                  {showCurrentPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Nueva Contraseña */}
            <div className="change-pwd-form-group">
              <label className="change-pwd-label">Nueva Contraseña</label>
              <div className="change-pwd-input-wrap">
                <KeyRound size={16} className="change-pwd-input-icon" />
                <input
                  required
                  disabled={isLoading}
                  type={showNewPassword ? 'text' : 'password'}
                  className="change-pwd-input"
                  placeholder="Mínimo 8 caracteres"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="change-pwd-eye-btn"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  tabIndex={-1}
                  aria-label={showNewPassword ? 'Ocultar' : 'Mostrar'}
                >
                  {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Confirmar Nueva Contraseña */}
            <div className="change-pwd-form-group">
              <label className="change-pwd-label">Confirmar Nueva Contraseña</label>
              <div className="change-pwd-input-wrap">
                <KeyRound size={16} className="change-pwd-input-icon" />
                <input
                  required
                  disabled={isLoading}
                  type={showConfirmPassword ? 'text' : 'password'}
                  className={`change-pwd-input ${
                    confirmPassword.length > 0
                      ? passwordsMatch
                        ? 'is-valid'
                        : 'is-invalid'
                      : ''
                  }`}
                  placeholder="Repite la nueva contraseña"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="change-pwd-eye-btn"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                  aria-label={showConfirmPassword ? 'Ocultar' : 'Mostrar'}
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Requirements checklist */}
            <div className="change-pwd-requirements">
              <div className={`change-pwd-req-item ${hasMinLength ? 'req-met' : ''}`}>
                <Check size={13} className="req-check" />
                <span>Al menos 8 caracteres</span>
              </div>
              <div className={`change-pwd-req-item ${hasLetter && hasNumberOrSpecial ? 'req-met' : ''}`}>
                <Check size={13} className="req-check" />
                <span>Letras y números o símbolos</span>
              </div>
              <div className={`change-pwd-req-item ${passwordsMatch ? 'req-met' : ''}`}>
                <Check size={13} className="req-check" />
                <span>Las contraseñas coinciden</span>
              </div>
            </div>

            {/* Footer buttons */}
            <div className="change-pwd-footer">
              {onCancel && (
                <button
                  type="button"
                  disabled={isLoading}
                  className="change-pwd-btn change-pwd-btn-cancel"
                  onClick={onCancel}
                >
                  {isMandatory ? 'CERRAR SESIÓN' : 'CANCELAR'}
                </button>
              )}
              <button
                type="submit"
                disabled={isLoading || !isFormValid}
                className={`change-pwd-btn change-pwd-btn-submit ${isLoading ? 'is-loading' : ''}`}
              >
                {isLoading ? (
                  <>
                    <Loader2 size={16} className="change-pwd-spinner" />
                    <span>ACTUALIZANDO...</span>
                  </>
                ) : (
                  <span>ACTUALIZAR Y ENTRAR</span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ChangePasswordModal;
